<template>
  <!-- Breadcrumb text hight + breadcrumb padding -->
  <div
    :style="`height: ${reserveSpaceForBreadcrumb ? 'calc(100% - 1.15625em - 2rem)' : '100%'}; min-height: calc(10rem - 1.15625em - 2rem)`"
  >
    <DataTable
      v-model:multi-sort-meta="multiSortMeta"
      v-model:expanded-rows="expandedRows"
      data-key="key"
      :selection="selectedFiles"
      :selectable-row="isSelectableFile"
      :expander-button-attrs="getExpanderButtonAttrs"
      clickable-rows
      :column-sizing="{
        mimeType: 'content',
        name: 'fill',
        createdAtISO: 'content',
        labelPath: 'content',
        edit: 'content',
      }"
      class="table-lisa w-100 FMFolderTableComponent-table"
      size="small"
      :scroll-height="rowsPerPageWithEnoughContent != null ? undefined : 'flex'"
      :virtual-scroller-options="
        rowsPerPageWithEnoughContent != null ? undefined : { itemSize: 30 }
      "
      :data-testid="testID()"
      :loading="loadingComputed"
      :scrollable="true"
      :selection-mode="fileSelectMode ? (singleSelect ? 'single' : 'multiple') : undefined"
      style="
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        min-width: 0;
        overflow-y: auto;
        max-height: 100%;
      "
      :paginator="rowsPerPageWithEnoughContent != null"
      :rows="rowsPerPageWithEnoughContent"
      :rows-per-page-options="
        rowsPerPageWithEnoughContent != null
          ? new Array(4).fill(0).map((_, i) => (i + 1) * rowsPerPageWithEnoughContent!)
          : []
      "
      :value="filesAndFolders"
      @update:selection="selctionChanged"
      @row-click="rowClicked($event.data)"
    >
      <Column field="mimeType" sortable :header="t('global.type')">
        <template #body="slotProps">
          <i v-bind="getFolderFileIcon(slotProps.data)" />
        </template>
      </Column>
      <Column field="name" sortable :header="t('global.name')">
        <template #body="slotProps">
          <span :data-testId="testID(`folder|${slotProps.data.name}`)">{{
            slotProps.data.name
          }}</span>
        </template>
      </Column>
      <Column v-if="folderId !== -1" field="createdAtISO" sortable :header="t('global.createdAt')">
        <template #body="slotProps">
          <div class="text-nowrap">
            {{ slotProps.data.createdAt }}
          </div>
        </template>
      </Column>
      <Column
        v-else
        field="labelPath"
        sortable
        :header="t('modules.filemanagement.components.FMFolderPreviewComponent.labelPath')"
      >
        <template #body="slotProps">
          <div class="text-nowrap">
            {{ slotProps.data.labelPath ?? t("global.na") }}
            <LISAButton
              v-if="slotProps.data.mimeType !== FOLDER_MIME_TYPE"
              v-tooltip.left="
                t('modules.filemanagement.components.FMFolderTableComponent.openFolder')
              "
              size="small"
              :text="true"
              icon="fas fa-folder-open"
              class="p-0"
              :disabled="slotProps.data.containingFolderId == null"
              @click="
                $router.push({
                  name: 'FileExplorer',
                  params: { selectedFolderId: slotProps.data.containingFolderId },
                })
              "
            />
          </div>
        </template>
      </Column>
      <Column field="edit">
        <template #body="slotProps">
          <div class="flex gap-1 justify-content-start">
            <span
              v-show="markFovorites != null"
              class="favorite-star-wrap"
              :class="{ 'favorite-star-wrap--burst': favoriteBurstKeys.has(slotProps.data.key) }"
              :style="
                slotProps.data.mimeType === FOLDER_MIME_TYPE
                  ? ''
                  : 'opacity: 0; pointer-events: none;'
              "
            >
              <LISAButton
                :icon="`fa${markFovorites?.includes(slotProps.data.folderId) ? 's' : 'r'} fa-star`"
                :data-testid="testID(`folder|${slotProps.data.name}|star`)"
                size="small"
                class="py-0 favorite-star-button"
                :text="true"
                @click.stop="favoriteClicked(slotProps.data)"
              />
              <span v-for="particle in 10" :key="particle" class="favorite-particle" />
            </span>
          </div>
        </template>
      </Column>

      <Column expander expand-icon="fa-solid fa-bars" collapse-icon="fa-solid fa-bars" />
      <template #expansion="slotProps">
        <div
          :data-testid="testID(`folder|${slotProps.data.name}|details|content`)"
          class="flex justify-content-between gap-5 w-100 px-2"
        >
          <div>
            <div class="twoColumnGrid">
              <div>{{ t("global.name") }}</div>
              <div>
                <div v-if="isLISA3">
                  <LISAInput
                    :model-value="
                      fileFolderNameEditString[slotProps.data.key] ?? slotProps.data.name
                    "
                    hide-label
                    :disabled="disabledEdit || isFileOrFolderDisabled(slotProps.data) || loading"
                    @update:model-value="
                      fileFolderNameEditString[slotProps.data.key] = String($event)
                    "
                  >
                    <template #append>
                      <LISAButton
                        icon="fas fa-save"
                        severity="success"
                        :disabled="
                          disabledEdit ||
                          isFileOrFolderDisabled(slotProps.data) ||
                          !fileFolderNameEditString[slotProps.data.key] ||
                          loading
                        "
                        text
                        full-width
                        full-height
                        @click="
                          saveName(
                            slotProps.data.key,
                            fileFolderNameEditString[slotProps.data.key] ?? slotProps.data.name,
                          )
                        "
                      />
                    </template>
                  </LISAInput>
                </div>
                <div v-else class="flex justify-content-between">
                  <InputText
                    :model-value="
                      fileFolderNameEditString[slotProps.data.key] ?? slotProps.data.name
                    "
                    size="small"
                    :disabled="disabledEdit || isFileOrFolderDisabled(slotProps.data) || loading"
                    style="
                      border-bottom-right-radius: 0;
                      border-top-right-radius: 0;
                      min-width: 20em;
                    "
                    @update:model-value="fileFolderNameEditString[slotProps.data.key] = $event"
                  />
                  <LISAButton
                    icon="fas fa-save"
                    severity="success"
                    :disabled="
                      disabledEdit ||
                      isFileOrFolderDisabled(slotProps.data) ||
                      !fileFolderNameEditString[slotProps.data.key] ||
                      loading
                    "
                    size="small"
                    class="lisaui:shrink-0"
                    style="border-bottom-left-radius: 0; border-top-left-radius: 0"
                    @click="
                      saveName(
                        slotProps.data.key,
                        fileFolderNameEditString[slotProps.data.key] ?? slotProps.data.name,
                      )
                    "
                  />
                </div>
              </div>
              <div>{{ t("global.type") }}</div>
              <div v-if="slotProps.data.mimeType === FOLDER_MIME_TYPE">
                {{ t("documents.searchOptions.Folder") }}
              </div>
              <div v-else>{{ slotProps.data.mimeType }}</div>
              <div>{{ t("global.createdBy") }}</div>
              <div>{{ slotProps.data.createdBy }} ({{ slotProps.data.createdAt }})</div>
              <div>{{ t("global.updatedBy") }}</div>
              <div>{{ slotProps.data.updatedBy }} ({{ slotProps.data.updatedAt }})</div>
            </div>
          </div>
          <div class="flex justify-content-between gap-2">
            <div class="flex flex-column justify-content-end">
              <FMFilePreview
                v-if="slotProps.data.mimeType !== FOLDER_MIME_TYPE"
                :file="slotProps.data"
                class="cursor-pointer"
                :hide-without-thumbnail="!fileSelectMode"
                size-y="8em"
                size-x="unset"
                @click="selectedEntity = slotProps.data"
              />
              <div class="flex justify-content-end gap-2 w-100 pt-2">
                <LISAButton
                  v-tooltip.top="t('global.close')"
                  :outlined="true"
                  icon="fas fa-times"
                  @click="collapseRow(slotProps.data)"
                />
                <LISAButton
                  v-if="!hideMoveDeleteButtons"
                  v-tooltip.top="{
                    value: !isFileOrFolderDisabled(slotProps.data)
                      ? t('global.delete')
                      : t(
                          'modules.filemanagement.components.FMFolderPreviewComponent.deleteTooltip',
                        ),
                  }"
                  icon="fas fa-trash-alt"
                  :data-testid="testID(`folder|${slotProps.data.name}|delete`)"
                  :disabled="isFileOrFolderDisabled(slotProps.data)"
                  severity="danger"
                  :outlined="true"
                  @click.stop="deleteFileOrFolder(slotProps.data)"
                />
                <FMFolderSelect
                  v-if="!hideMoveDeleteButtons"
                  v-tooltip.top="{
                    value: !isFileOrFolderDisabled(slotProps.data)
                      ? t('modules.filemanagement.components.FMFolderTableComponent.move')
                      : t(
                          'modules.filemanagement.components.FMFolderPreviewComponent.folderSelectTooltip',
                        ),
                  }"
                  :data-testid="testID(`folder|${slotProps.data.name}|move`)"
                  :clearable="false"
                  :disabled="isFileOrFolderDisabled(slotProps.data)"
                  :show-as-button="true"
                  @update:model-value="moveFileOrFolder(slotProps.data, $event)"
                >
                  <template #button="args">
                    <LISAButton v-bind="args" :outlined="true">
                      <span class="fa-stack" style="font-size: 0.575em">
                        <i class="fa-solid fa-folder fa-stack-2x"></i>
                        <i class="fa-solid fa-arrow-right fa-stack-1x fa-inverse"></i>
                      </span>
                    </LISAButton>
                  </template>
                </FMFolderSelect>
                <LISAButton
                  v-if="slotProps.data.fileUUID != null"
                  v-tooltip.top="{
                    value: t('global.download'),
                  }"
                  icon="fas fa-download"
                  class="py-0"
                  @click.stop="
                    downloadFile(
                      slotProps.data.fileUUID!,
                      slotProps.data.name,
                      slotProps.data.originalFileEnding,
                    )
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="!hideUpload || showLoadMore" #footer>
        <FMFileUpload
          v-if="!hideUpload"
          :key="folderId"
          v-model:upload-in-progress="uploadInProgress"
          :mime-type-filters="mimeTypeFilters"
          :prevent-copy-paste="preventCopyPaste"
          :refetch-await="refetchAwait"
          :folder-id="folderId"
          :max-file-upload="maxFileUpload"
          :data-testid="testID('upload')"
          @file-created="filesUploaded"
        />
        <div v-if="showLoadMore" class="flex justify-content-center">
          <LISAButton
            v-if="showLoadMore"
            class="w-100"
            :disabled="loading || disabledLoadMore"
            @click="$emit('loadMore')"
          >
            {{ t("modules.filemanagement.view.FMFileExplorer.loadMore") }}
          </LISAButton>
        </div>
      </template>
    </DataTable>
    <FMFileViewerModal
      v-if="selectedEntity != null"
      :visible="true"
      :files="[selectedEntity]"
      @update:visible="changePreviewModalVisibility"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { FMDocumentTileSubscriptionType, type FMFolderContentFileFragment } from "@/graphql";
import { downloadFile } from "./FMFileUploadHelper";
import { computed, ref, useAttrs, watch, type PropType } from "vue";
import type { DataTableExpandedRows, DataTableSortMeta } from "@/components/shared/dataTableTypes";
import FMFileViewerModal from "./FMFileViewerModal.vue";
import FMFileUpload from "./FMFileUpload.vue";
import DataTable from "@/components/shared/LISADataTableAdapter.vue";
import { Column } from "@/components/shared/LISADataTableDeclarations";
import FMFilePreview from "./FMFilePreview.vue";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import notEmpty from "@/functions/notEmpty";
import {
  FOLDER_MIME_TYPE,
  getFolderFileIcon as getFolderFileIconHelper,
  type FolderTableContent,
} from "../helper/FMFilePreviewHelper";
import { getFilesInFilter } from "../helper/FMDocumentFilterHelper";
import type { DocumentNode } from "graphql";
import FMFolderSelect from "./FMFolderSelect.vue";
import {
  getFolderOrFileTypeColor,
  useFileOrFolderOperations,
  useFMFolderNames,
  type FMFolderTableMovedFolder,
} from "../helper/FMFolderHelper";
import vTooltip from "primevue/tooltip";
import InputText from "primevue/inputtext";
import { isLISA3 } from "@/composables/useLISA3.ts";
import { LISAInput } from "@fi-solutions/lisa-ui";

const props = defineProps({
  filesAndFolders: {
    type: Array as PropType<FolderTableContent[]>,
    required: true,
  },
  folderId: {
    type: Number,
    required: true,
  },
  fileSelectMode: {
    type: Boolean,
    required: false,
    default: true,
  },
  preventCopyPaste: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideFolders: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideUpload: {
    type: Boolean,
    required: false,
    default: false,
  },
  rowsPerPage: {
    type: Number,
    required: false,
    default: undefined,
  },
  // Only works in fileSelectMode and will be ignored otherwise
  showFilterType: {
    type: Object as PropType<{
      type: FMDocumentTileSubscriptionType;
      filesShownCount: number;
    }>,
    required: false,
    default: undefined,
  },
  refetchAwait: {
    type: Array as PropType<
      {
        query: DocumentNode;
        variables: any;
      }[]
    >,
    required: false,
    default: undefined,
  },
  markFovorites: {
    type: Array as PropType<number[]>,
    required: false,
    default: undefined,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  showLoadMore: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledLoadMore: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledEdit: {
    type: Boolean,
    required: false,
    default: false,
  },
  mimeTypeFilters: {
    type: String,
    required: false,
    default: undefined,
  },
  hideMoveDeleteButtons: {
    type: Boolean,
    required: false,
    default: false,
  },
  reserveSpaceForBreadcrumb: {
    type: Boolean,
    required: false,
    default: true,
  },
  singleSelect: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxFileUpload: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const uploadInProgress = defineModel("uploadInProgress", {
  type: Boolean,
  required: false,
  default: false,
});

const attrs = useAttrs();

const testID = computed(() => {
  if (attrs["data-testid"]) {
    return (name?: string) => `${attrs["data-testid"]}|${name ?? "FMFolderTableComponent"}`;
  }
  return (name?: string) => name ?? "FMFolderTableComponent";
});

function getExpanderButtonAttrs(row: FolderTableContent) {
  return {
    "data-testid": testID.value(`folder|${row.name}|details`),
    title: expandedRows.value[row.key]
      ? t("modules.filemanagement.components.FMFolderPreviewComponent.collapseTooltip")
      : t("modules.filemanagement.components.FMFolderPreviewComponent.expandTooltip"),
  };
}

const multiSortMeta = ref<DataTableSortMeta[]>([
  { field: "mimeType", order: 1 },
  { field: "name", order: 1 },
]);

watch(
  () => props.folderId,
  () => {
    expandedRows.value = {};
  },
);

watch(
  computed(() => props.showFilterType),
  () => {
    if (props.showFilterType != null) {
      switch (props.showFilterType.type) {
        case FMDocumentTileSubscriptionType.ALL:
        case FMDocumentTileSubscriptionType.CUSTOM:
        case FMDocumentTileSubscriptionType.RECENTLY_CHANGED:
        case FMDocumentTileSubscriptionType.LAST_CHANGED:
          multiSortMeta.value = [{ field: "createdAtISO", order: -1 }];
      }
    } else {
      multiSortMeta.value = [
        { field: "mimeType", order: 1 },
        { field: "name", order: 1 },
      ];
    }
  },
  {
    immediate: true,
  },
);

const disableCheckboxes = computed(
  () =>
    props.showFilterType?.type != null &&
    props.showFilterType?.type !== FMDocumentTileSubscriptionType.CUSTOM,
);

function isSelectableFile(row: FolderTableContent): boolean {
  return !disableCheckboxes.value && row.mimeType !== FOLDER_MIME_TYPE;
}

const rowsPerPageWithEnoughContent = computed(() => {
  if (props.rowsPerPage == null || props.rowsPerPage >= props.filesAndFolders.length) {
    return undefined;
  }

  return props.rowsPerPage;
});

const expandedRows = ref<DataTableExpandedRows>({});

const selectedEntity = ref<(typeof props.filesAndFolders)[number]>();

const selectedFileIds = defineModel("selectedFileIds", {
  type: Array as PropType<number[]>,
  required: false,
  default: () => [],
});

const emit = defineEmits<{
  folderSelected: [folderId: number];
  filesSelected: [fileIds: number[]];
  fileCreated: [file: FMFolderContentFileFragment];
  fileDeleted: [fileId: number];
  folderDeleted: [folderId: number];
  folderMoved: [folder: FMFolderTableMovedFolder];
  loadMore: [];
}>();

// files will be immediately added to folder
async function filesUploaded(file: FMFolderContentFileFragment) {
  emit("fileCreated", file);

  // if selction mode is active the uploaded file is immediately added to selection
  if (props.fileSelectMode) {
    selectedFileIds.value = props.singleSelect
      ? [file.id]
      : [...new Set([...selectedFileIds.value, file.id])];
  }
}

const { loading: loadingFolderNames, getFolderStats, ready: folderNamesReady } = useFMFolderNames();

function getFolderFileIcon(fileOrFolder: FolderTableContent) {
  if (fileOrFolder.mimeType === FOLDER_MIME_TYPE && folderNamesReady.value) {
    const stats = getFolderStats({
      name: fileOrFolder.name,
      path: fileOrFolder.folderPath!,
      type: fileOrFolder.folderType!,
      siteId: fileOrFolder.siteId,
    });
    return { class: `${stats.icon} ${stats.colorClass}` };
  }
  return {
    class: getFolderFileIconHelper(fileOrFolder.mimeType),
    style: `color: ${getFolderOrFileTypeColor(fileOrFolder?.mimeType)}`,
  };
}

function rowClicked(element: (typeof props.filesAndFolders)[number]) {
  if (element == null) {
    return;
  }
  if (element?.mimeType === FOLDER_MIME_TYPE) {
    if (element.folderId != null) {
      emit("folderSelected", element.folderId);
    }
  } else {
    if (!props.fileSelectMode) {
      selectedEntity.value = element;
    }
  }
}

const selectedFiles = computed({
  get(): FolderTableContent[] | FolderTableContent | undefined {
    const files =
      props.showFilterType != null &&
      props.showFilterType.type !== FMDocumentTileSubscriptionType.CUSTOM
        ? (getFilesInFilter(
            props.filesAndFolders.filter((el) => el.fileId != null),
            props.showFilterType.type,
            props.showFilterType.filesShownCount,
            selectedFileIds.value,
          ) as FolderTableContent[])
        : props.filesAndFolders.filter(
            (el) => el.fileId != null && selectedFileIds.value.includes(el.fileId),
          );

    return props.singleSelect ? files[0] : files;
  },
  set(contents: FolderTableContent[] | FolderTableContent | undefined) {
    if (
      contents == null ||
      (props.showFilterType != null &&
        props.showFilterType.type !== FMDocumentTileSubscriptionType.CUSTOM)
    ) {
      return;
    }

    const files = Array.isArray(contents) ? contents : [contents];
    selectedFileIds.value = props.singleSelect
      ? files
          .map((el) => el.fileId)
          .filter(notEmpty)
          .slice(-1)
      : files.map((el) => el.fileId).filter(notEmpty);
  },
});

function changePreviewModalVisibility(visibility: boolean) {
  if (!visibility) {
    selectedEntity.value = undefined;
  }
}
async function selctionChanged(
  elements: (typeof selectedEntity)["value"][] | (typeof selectedEntity)["value"],
) {
  // The non array comes from the single select settins in the data table and will
  // be ignored and interpreted as open the preview modal over row-click

  if (
    !props.fileSelectMode ||
    elements == null ||
    (!Array.isArray(elements) && !props.singleSelect)
  ) {
    return;
  }

  if (!Array.isArray(elements)) {
    if (elements.fileId) {
      selectedFileIds.value = [elements.fileId];
    }
    return;
  }

  if (
    props.showFilterType != null &&
    props.showFilterType.type !== FMDocumentTileSubscriptionType.CUSTOM
  ) {
    return;
  }
  selectedFileIds.value = elements
    .filter((el) => el?.mimeType !== FOLDER_MIME_TYPE)
    .map((el) => el?.fileId)
    .filter(notEmpty);
}

const {
  deleteFileOrFolder: deleteFileOrFolderFunction,
  isFileOrFolderDisabled,
  moveFileOrFolderToFolder,
  updateNameOfFileOrFolder,
  toggleFavorite,
  fileFolderNameEditString,
  loading: loadingFileFolderDelete,
} = useFileOrFolderOperations(
  computed(() => ({ refetchAwait: props.refetchAwait, markFovorites: props.markFovorites })),
);

const favoriteBurstKeys = ref(new Set<string>());
const favoriteBurstTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function triggerFavoriteBurst(key: string) {
  const existingTimeout = favoriteBurstTimeouts.get(key);
  if (existingTimeout != null) {
    clearTimeout(existingTimeout);
  }

  favoriteBurstKeys.value = new Set([...favoriteBurstKeys.value, key]);
  favoriteBurstTimeouts.set(
    key,
    setTimeout(() => {
      const nextKeys = new Set(favoriteBurstKeys.value);
      nextKeys.delete(key);
      favoriteBurstKeys.value = nextKeys;
      favoriteBurstTimeouts.delete(key);
    }, 900),
  );
}

async function favoriteClicked(fileOrFolder: (typeof props.filesAndFolders)[number]) {
  const isAddingFavorite =
    fileOrFolder.mimeType === FOLDER_MIME_TYPE &&
    fileOrFolder.folderId != null &&
    !props.markFovorites?.includes(fileOrFolder.folderId);

  if (isAddingFavorite) {
    triggerFavoriteBurst(fileOrFolder.key);
  }

  await toggleFavorite(fileOrFolder);
}

async function moveFileOrFolder(
  fileOrFolder: (typeof props.filesAndFolders)[number],
  folderId: number | number[] | undefined,
) {
  const movedFolder = await moveFileOrFolderToFolder(fileOrFolder, folderId);

  if (
    fileOrFolder.mimeType !== FOLDER_MIME_TYPE ||
    movedFolder == null ||
    folderId == null ||
    typeof folderId !== "number"
  ) {
    return;
  }

  emit("folderMoved", {
    id: movedFolder.id,
    name: movedFolder.name,
    path: movedFolder.path,
    isLeafFolder: movedFolder.isLeafFolder,
    siteId: movedFolder.siteId,
    index: movedFolder.index,
    type: movedFolder.type,
    parentId: folderId,
    previousParentId: props.folderId > 0 ? props.folderId : undefined,
  });
}

async function deleteFileOrFolder(fileOrFolder: (typeof props.filesAndFolders)[number]) {
  const result = await deleteFileOrFolderFunction(fileOrFolder);
  if (!result) {
    return;
  }
  if (fileOrFolder.fileId != null) {
    selectedFileIds.value = selectedFileIds.value.filter(
      (fileId) => fileId !== fileOrFolder.fileId,
    );
    emit("fileDeleted", fileOrFolder.fileId);
  } else if (fileOrFolder.folderId != null) {
    emit("folderDeleted", fileOrFolder.folderId);
  }
}

async function saveName(key: string, newName: string) {
  const element = props.filesAndFolders.find((el) => el.key === key);

  await updateNameOfFileOrFolder(element, newName);
}

function collapseRow(fileOrFolder: (typeof props.filesAndFolders)[number]) {
  delete expandedRows.value[fileOrFolder.key];
}

const loadingComputed = computed(
  () => loadingFileFolderDelete.value || props.loading || loadingFolderNames.value,
);

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.twoColumnGrid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 0.5rem;
}

.expansion-side-menu {
  border: 1px solid var(--p-slate-300);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.125rem;
}

.favorite-star-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  isolation: isolate;
  overflow: visible;
}

.favorite-star-wrap--burst::before {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.42rem;
  height: 0.42rem;
  z-index: 2;
  content: "";
  background: #fff7a8;
  box-shadow:
    0 0 0 1px rgb(255 255 255 / 0.9),
    0 0 0.38rem #fff06a;
  opacity: 0;
  transform: translate(-50%, -50%) rotate(45deg) scale(0.2);
  pointer-events: none;
  animation: favorite-center-glitter 560ms ease-out;
}

.favorite-star-wrap--burst .favorite-star-button {
  animation: favorite-star-pop 360ms ease-out;
}

.favorite-particle {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  width: var(--favorite-particle-size, 0.3rem);
  height: var(--favorite-particle-size, 0.3rem);
  pointer-events: none;
  border-radius: 1px;
  opacity: 0;
  background: currentColor;
  box-shadow:
    0 0 0 1px rgb(255 255 255 / 0.9),
    0 0 0.42rem currentColor,
    0 0 0.72rem currentColor;
  transform: translate(-50%, -50%) rotate(45deg) scale(0.25);
}

.favorite-particle::before,
.favorite-particle::after {
  position: absolute;
  inset: 50% auto auto 50%;
  width: 0.09rem;
  height: calc(var(--favorite-particle-size, 0.3rem) * 1.9);
  content: "";
  background: currentColor;
  border-radius: 999px;
  box-shadow: 0 0 0.28rem currentColor;
  transform: translate(-50%, -50%);
}

.favorite-particle::after {
  width: calc(var(--favorite-particle-size, 0.3rem) * 1.9);
  height: 0.09rem;
}

.favorite-star-wrap--burst .favorite-particle {
  animation: favorite-glitter-burst 520ms ease-out forwards;
}

.favorite-particle:nth-of-type(1) {
  --favorite-particle-x: 0rem;
  --favorite-particle-y: -1.32rem;
  --favorite-particle-size: 0.34rem;
  color: #fff7a8;
  animation-delay: 20ms;
}

.favorite-particle:nth-of-type(2) {
  --favorite-particle-x: 1.22rem;
  --favorite-particle-y: -0.72rem;
  --favorite-particle-size: 0.28rem;
  color: #7df9ff;
  animation-delay: 55ms;
}

.favorite-particle:nth-of-type(3) {
  --favorite-particle-x: 1.18rem;
  --favorite-particle-y: 0.82rem;
  --favorite-particle-size: 0.32rem;
  color: #ffb7f5;
  animation-delay: 5ms;
}

.favorite-particle:nth-of-type(4) {
  --favorite-particle-x: -0.08rem;
  --favorite-particle-y: 1.34rem;
  --favorite-particle-size: 0.26rem;
  color: #ffffff;
  animation-delay: 70ms;
}

.favorite-particle:nth-of-type(5) {
  --favorite-particle-x: -1.24rem;
  --favorite-particle-y: 0.56rem;
  --favorite-particle-size: 0.31rem;
  color: #b9ff8a;
  animation-delay: 35ms;
}

.favorite-particle:nth-of-type(6) {
  --favorite-particle-x: -1.08rem;
  --favorite-particle-y: -0.84rem;
  --favorite-particle-size: 0.29rem;
  color: #ffd36e;
  animation-delay: 85ms;
}

.favorite-particle:nth-of-type(7) {
  --favorite-particle-x: 0.58rem;
  --favorite-particle-y: -1.18rem;
  --favorite-particle-size: 0.24rem;
  color: #ffffff;
  animation-delay: 115ms;
}

.favorite-particle:nth-of-type(8) {
  --favorite-particle-x: 1.36rem;
  --favorite-particle-y: 0.08rem;
  --favorite-particle-size: 0.24rem;
  color: #ffef73;
  animation-delay: 95ms;
}

.favorite-particle:nth-of-type(9) {
  --favorite-particle-x: -0.58rem;
  --favorite-particle-y: 1.16rem;
  --favorite-particle-size: 0.23rem;
  color: #9fffff;
  animation-delay: 130ms;
}

.favorite-particle:nth-of-type(10) {
  --favorite-particle-x: -1.36rem;
  --favorite-particle-y: -0.06rem;
  --favorite-particle-size: 0.25rem;
  color: #ffb7f5;
  animation-delay: 105ms;
}

@keyframes favorite-center-glitter {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(45deg) scale(0.15);
  }

  28% {
    opacity: 1;
    transform: translate(-50%, -50%) rotate(45deg) scale(1);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(45deg) scale(0.35);
  }
}

@keyframes favorite-star-pop {
  0% {
    transform: scale(1);
  }

  45% {
    transform: scale(1.12) rotate(-4deg);
  }

  100% {
    transform: scale(1) rotate(0deg);
  }
}

@keyframes favorite-glitter-burst {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(45deg) scale(0.2);
  }

  18% {
    opacity: 1;
    transform: translate(
        calc(-50% + (var(--favorite-particle-x) * 0.32)),
        calc(-50% + (var(--favorite-particle-y) * 0.32))
      )
      rotate(45deg) scale(1.25);
  }

  46% {
    opacity: 0.22;
  }

  62% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(
        calc(-50% + var(--favorite-particle-x)),
        calc(-50% + var(--favorite-particle-y))
      )
      rotate(45deg) scale(0.45);
  }
}
</style>
<style lang="scss">
.FMFolderTableComponent-table {
  th {
    background-color: var(--p-gray-200) !important;
    border-color: var(--p-gray-400) !important;
    color: var(--p-black) !important;
  }
}
</style>
